import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { TERMS_OF_SERVICE_URL } from "@unbox/api";
import { Eye, EyeOff } from "@untitledui/icons";
import { type ReactNode, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod/v3";
import { useAcceptInvite } from "@/api/auth/accept-invite";
import { useValidateInvite } from "@/api/auth/validate-invite";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { HintText } from "@/components/base/input/hint-text";
import { Input, InputBase, TextField } from "@/components/base/input/input";
import { Label } from "@/components/base/input/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Form, FormField } from "@/components/ui/form";
import { authClient } from "@/lib/auth-client";
import { useAuth } from "@/providers/auth-provider";
import { acceptInviteRoute } from "@/router";

const formSchema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  password: z
    .string()
    .min(8, { message: "Password must be at least 8 characters" })
    .regex(/[a-zA-Z]/, { message: "Password must contain at least one letter" })
    .regex(/\d/, { message: "Password must contain at least one number" })
    .regex(/[@#!$%&*_]/, { message: "Password must contain at least one special character" }),
  tos: z.boolean().refine((val) => val === true, {
    message: "You must accept the terms",
  }),
});

const PASSWORD_HINT = "Use a minimum of 8+ characters with a mix of letters, numbers, and symbols";

const Header = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <div className="flex flex-col gap-3">
    <h1 className="text-display-md font-semibold tracking-[-0.72px] text-primary">{title}</h1>
    <p className="text-md text-tertiary">{subtitle}</p>
  </div>
);

const Inner = () => {
  const form = useForm({ resolver: zodResolver(formSchema), defaultValues: { tos: false } });
  const { onSignIn } = useAuth();
  const [signingIn, setSigningIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { token } = acceptInviteRoute.useSearch() as { token: string };
  const { data: validation, isLoading: isValidating } = useValidateInvite(token);
  const { mutateAsync: acceptInvite, isPending, error } = useAcceptInvite();

  const handleSignUp = async (payload: z.infer<typeof formSchema>) => {
    try {
      const { email } = await acceptInvite({ ...payload, token });
      setSigningIn(true);
      const { error: signInError } = await authClient.signIn.email({ email, password: payload.password });
      if (signInError) throw new Error(signInError.message || "Log in failed");
      await onSignIn();
      navigate({ to: "/" });
    } catch (e) {
      setSigningIn(false);
      throw e;
    }
  };
  if (isValidating || signingIn) return <LoadingIndicator />;
  if (validation?.status !== "valid")
    return (
      <div className="flex w-full max-w-[360px] flex-col gap-8" data-testid="invalid-invitation">
        <Header title="Invalid invite" subtitle="Your invite may have expired or been revoked" />
        <Button href="/login" type="button" color="secondary" size="lg" className="w-full">
          Back to log in
        </Button>
      </div>
    );

  const { errors } = form.formState;

  return (
    <div className="flex w-full max-w-[360px] flex-col gap-8" data-testid="accept-invitation-form">
      <Header title="Set up your account" subtitle="You're almost there! Complete your account setup below." />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSignUp)} className="flex flex-col gap-6">
          <div className="flex flex-col gap-5">
            <Input
              size="md"
              label="Email"
              type="email"
              autoComplete="email"
              data-testid="email-field"
              placeholder="Name@email.com"
              value={validation.invitation.email}
              isDisabled
            />

            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <Input
                  {...field}
                  size="md"
                  label="First name"
                  autoComplete="given-name"
                  data-testid="first-name-field"
                  isRequired
                  hideRequiredIndicator
                  isInvalid={!!errors.firstName}
                />
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <Input
                  {...field}
                  size="md"
                  label="Last name"
                  autoComplete="family-name"
                  data-testid="last-name-field"
                  isRequired
                  hideRequiredIndicator
                  isInvalid={!!errors.lastName}
                />
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field: { ref, ...field } }) => (
                <TextField
                  {...field}
                  size="md"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  isRequired
                  isInvalid={!!errors.password}
                >
                  <Label isRequired={false}>Password</Label>
                  <div className="relative w-full">
                    <InputBase ref={ref} data-testid="password-field" inputClassName="pr-10" />
                    <button
                      type="button"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute top-1/2 right-3.5 -translate-y-1/2 text-fg-quaternary hover:text-fg-quaternary_hover"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </button>
                  </div>
                  <HintText isInvalid={!!errors.password}>
                    {errors.password ? (
                      <span data-testid="password-error">{errors.password.message}</span>
                    ) : (
                      PASSWORD_HINT
                    )}
                  </HintText>
                </TextField>
              )}
            />

            <div className="flex flex-col gap-1.5">
              <Checkbox
                data-testid="tos-field"
                isSelected={form.watch("tos")}
                onChange={(val) => form.setValue("tos", val, { shouldValidate: true, shouldDirty: true })}
                label={
                  <>
                    I agree to the <TermsLink>Terms of service</TermsLink>
                  </>
                }
              />
              {errors.tos && (
                <p className="text-sm text-error-primary" data-testid="tos-error">
                  {errors.tos.message}
                </p>
              )}
            </div>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error?.message}</AlertDescription>
            </Alert>
          )}

          <Button type="submit" size="lg" isDisabled={isPending} className="w-full" data-testid="submit">
            {isPending ? "Creating account..." : "Create account"}
          </Button>
        </form>
      </Form>

      <div className="flex items-start justify-center gap-1">
        <p className="text-sm text-tertiary">Already have an account?</p>
        <Button color="link-color" size="md" href="/login">
          Log in
        </Button>
      </div>
    </div>
  );
};

const TermsLink = ({ children }: { children: ReactNode }) => (
  <a
    href={TERMS_OF_SERVICE_URL}
    target="_blank"
    rel="noopener noreferrer"
    className="underline hover:no-underline"
    onClick={(event) => event.stopPropagation()}
  >
    {children}
  </a>
);

export const AcceptInvitation = () => {
  return (
    <div className="flex min-h-screen bg-primary" data-testid="accept-invitation-page">
      <div className="hidden p-4 lg:flex lg:w-1/2">
        <div className="relative flex-1 overflow-hidden rounded-2xl border border-black/10 bg-linear-[151deg,#1b1525_14.5%,#2a1e32_89.5%]">
          <img
            src="/auth/invite-panel.png"
            alt=""
            className="absolute inset-0 h-full w-full object-contain object-right-bottom"
          />
          <img
            src="/auth/unbox-logo-white.png"
            alt="Unbox"
            className="absolute top-[50px] left-[54px] h-12 w-[174px]"
          />
        </div>
      </div>

      <div className="flex w-full items-center justify-center px-8 py-12 lg:w-1/2">
        <Inner />
      </div>
    </div>
  );
};
